/**
 * product-family-panel.js
 *
 * Handles gallery injection and no-reload switching for:
 *   1. Piece switching within a collection (SRA, same gallery)
 *   2. Color/collection switching between same-family collections (SRA, gallery rebuild)
 *
 * Expects a <script type="application/json" id="family-panel-data-{productId}">
 * block rendered by snippets/product-family-panel.liquid with shape:
 *   { productId, sectionId, heroImage, members: [{ id, handle, url, title, price, isCurrent, images }] }
 */

// ─── Utilities ───────────────────────────────────────────────────────────────

function escHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function swapElement(selector, fromDoc) {
  const src = fromDoc.querySelector(selector);
  const dst = document.querySelector(selector);
  if (src && dst) dst.replaceWith(src);
}

function updatePanelActiveState(panel, memberId) {
  panel.querySelectorAll('[data-family-member-id]').forEach(btn => {
    const isTarget = String(btn.dataset.familyMemberId) === String(memberId);
    btn.classList.toggle('product-family-panel_option-btn--active', isTarget);
    if (isTarget) btn.setAttribute('aria-current', 'page');
    else btn.removeAttribute('aria-current');
  });
}

function waitForSplide(el, cb, attempts = 0) {
  if (el.splide) { cb(); return; }
  if (attempts > 100) return;
  setTimeout(() => waitForSplide(el, cb, attempts + 1), 30);
}

// ─── Slide builders ──────────────────────────────────────────────────────────

function buildMainSlide(src, srcLarge, alt, imgWidth, imgHeight, ratio, memberId, isHero) {
  const r = ratio > 0 ? ratio : 4 / 3;
  const tag = isHero
    ? 'data-hero-image="true"'
    : `data-family-member-id="${escHtml(String(memberId))}"`;
  const pswpW = imgWidth || 1200;
  const pswpH = imgHeight || Math.round(pswpW / r);
  return `<div class="splide__slide product-family-panel_injected-slide" ${tag}>
  <a href="${escHtml(srcLarge || src)}"
     class="product-gallery-thumbnail --media-zoom-in --media-size-adapt"
     style="--media-ratio: ${r.toFixed(4)};"
     data-pswp-width="${pswpW}"
     data-pswp-height="${pswpH}">
    <img src="${escHtml(src)}" alt="${escHtml(alt)}" loading="lazy"
         width="1100" height="${Math.round(1100 / r)}"
         class="object-fit object-fit--absolute">
  </a>
</div>`;
}

function buildThumbSlide(src, alt, memberId, isHero) {
  const tag = isHero
    ? 'data-hero-image="true"'
    : `data-family-member-id="${escHtml(String(memberId))}"`;
  return `<div class="splide__slide product-family-panel_injected-slide" ${tag}>
  <div class="product-switcher-thumbnail-holder">
    <div class="product-switcher-thumbnail">
      <img src="${escHtml(src)}" alt="${escHtml(alt)}" loading="lazy"
           class="object-fit object-fit--absolute">
    </div>
  </div>
</div>`;
}

// ─── Measurements section ────────────────────────────────────────────────────
// The specs table is its OWN template section, so it must be fetched and
// swapped alongside the product section or the dimensions go stale on every
// no-reload switch. Its id comes from a marker the section always renders.

const measRoot = document.querySelector('[data-product-measurements-section]');
const measurementsSectionId = measRoot ? measRoot.dataset.productMeasurementsSection : null;

function swapMeasurements(html) {
  if (!measurementsSectionId || html == null) return;
  const wrapper = document.getElementById(`shopify-section-${measurementsSectionId}`);
  if (!wrapper) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = html; // SRA returns the section INCLUDING its wrapper div
  const fresh = tmp.firstElementChild;
  if (fresh) wrapper.replaceWith(fresh);
}

// ─── SRA fetch ───────────────────────────────────────────────────────────────
// One request renders BOTH sections (SRA takes comma-separated ids).
// Returns { doc, measurementsHtml } or null.

function fetchSectionDoc(url, sectionId) {
  const ids = measurementsSectionId ? `${sectionId},${measurementsSectionId}` : sectionId;
  return fetch(`${url}?sections=${ids}`)
    .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
    .then(json => {
      const html = json[sectionId];
      if (!html) throw new Error(`Key "${sectionId}" missing. Got: ${Object.keys(json).join(', ')}`);
      return {
        doc: new DOMParser().parseFromString(html, 'text/html'),
        measurementsHtml: measurementsSectionId ? json[measurementsSectionId] : null,
      };
    })
    .catch(err => { console.warn('[family-panel] SRA fetch failed:', err); return null; });
}

// Prefetch cache — entries deleted before awaiting so a consumed doc is never reused
const prefetchCache = new Map();

function prefetchDoc(url, sectionId) {
  if (prefetchCache.has(url)) return;
  prefetchCache.set(url, fetchSectionDoc(url, sectionId));
}

// ─── Gallery state (module-level, one panel per page) ────────────────────────

const gallery = { main: null, thumb: null, thumbEl: null };

// Tracks the piece name (option1 / title) of the member the user is currently
// viewing so that color switches can navigate to the same piece in the new collection.
let activePieceName = null;

// Rebuilds the Splide gallery from scratch using the given panel data.
// Called on initial load and after every color switch.
function buildGallery(data) {
  const { heroImage, members } = data;
  const { main, thumb, thumbEl } = gallery;
  if (!main || !thumb) return;

  for (let i = main.length - 1; i >= 0; i--) main.remove(i);
  for (let i = thumb.length - 1; i >= 0; i--) thumb.remove(i);

  const seenSrcs = new Set();
  let slideIdx = 0;
  let currentFirstIdx = 0;

  if (heroImage) {
    const heroRatio = heroImage.height > 0 ? heroImage.width / heroImage.height : 4 / 3;
    main.add(buildMainSlide(heroImage.src, heroImage.srcLarge, 'Collection Overview',
      heroImage.width, heroImage.height, heroRatio, null, true));
    thumb.add(buildThumbSlide(heroImage.src, 'Collection Overview', null, true));
    seenSrcs.add(heroImage.src);
    slideIdx++;
  }

  for (const member of members) {
    let isFirst = true;
    for (const image of member.images) {
      if (seenSrcs.has(image.src)) continue;
      seenSrcs.add(image.src);
      const ratio = image.height > 0 ? image.width / image.height : 1;
      main.add(buildMainSlide(image.src, image.srcLarge, image.alt,
        image.width, image.height, ratio, member.id, false));
      thumb.add(buildThumbSlide(image.src, image.alt, member.id, false));
      if (member.isCurrent && isFirst) currentFirstIdx = slideIdx;
      isFirst = false;
      slideIdx++;
    }
  }

  thumbEl.classList.remove('visually-hidden');
  main.go(currentFirstIdx);
}

// ─── Gallery fade helpers ────────────────────────────────────────────────────
// Used when a rebuild would land on the hero with no slide animation (the target
// piece has no images) — a cross-fade reads as a transition instead of a snap.

function getGalleryWrap() {
  const { main } = gallery;
  if (!main || !main.root) return null;
  return main.root.closest('splide-slider') || main.root;
}

function fadeOutGallery(duration = 180) {
  const wrap = getGalleryWrap();
  if (!wrap) return Promise.resolve();
  wrap.style.transition = `opacity ${duration}ms ease`;
  wrap.style.opacity = '0';
  return new Promise(resolve => setTimeout(resolve, duration + 20));
}

function fadeInGallery(duration = 220) {
  const wrap = getGalleryWrap();
  if (!wrap) return;
  wrap.style.transition = `opacity ${duration}ms ease`;
  requestAnimationFrame(() => {
    wrap.style.opacity = '1';
    setTimeout(() => {
      wrap.style.transition = '';
      wrap.style.opacity = '';
    }, duration + 60);
  });
}

function resetGalleryFade() {
  const wrap = getGalleryWrap();
  if (!wrap) return;
  wrap.style.transition = '';
  wrap.style.opacity = '';
}

// ─── Navigation: piece switching ─────────────────────────────────────────────

// Generation counter — incremented on every click so stale async results are discarded
let selectGeneration = 0;

async function selectMember(member, panel, sectionId, generation) {
  if (member.pieceName != null) activePieceName = member.pieceName;
  const { main } = gallery;

  // 1. Navigate gallery immediately. If this piece has no images of its own,
  // glide to the collection hero rather than lingering on the previous piece's photo.
  const slides = Array.from(main.root.querySelectorAll('.splide__slide'));
  const targetIdx = slides.findIndex(s => s.dataset.familyMemberId === String(member.id));
  if (targetIdx >= 0) {
    main.go(targetIdx);
  } else if (slides.length && slides[0].dataset.heroImage) {
    main.go(0);
  }

  // 2. Update URL and active state immediately
  history.pushState({ familyMemberId: member.id }, '', member.url);
  updatePanelActiveState(panel, member.id);

  // 3. Optimistic title update
  const titleEl = document.querySelector('.main-product_main-heading');
  if (titleEl) titleEl.textContent = member.title;

  // 4. Consume prefetch cache (delete first so gutted doc is never reused)
  const cachedPromise = prefetchCache.get(member.url);
  prefetchCache.delete(member.url);
  const res = await (cachedPromise ?? fetchSectionDoc(member.url, sectionId));
  if (!res || generation !== selectGeneration) return;
  const doc = res.doc;

  // 5. Swap product details
  const srcJson = doc.querySelector('script[id^="ProductCardJson-"]');
  const dstJson = document.querySelector('script[id^="ProductCardJson-"]');
  if (srcJson && dstJson) dstJson.replaceWith(srcJson);

  swapElement('product-variants', doc);
  swapElement('atc-submit-form', doc);
  swapElement('[data-render-price]', doc);
  swapElement('[data-render-sku]', doc);
  swapElement('.main-product_main-heading', doc);
  swapElement('.main-product_row--content', doc);

  // 6. Re-render the (separate) measurements section for the new product
  swapMeasurements(res.measurementsHtml);

  const newTitle = doc.querySelector('title');
  if (newTitle) document.title = newTitle.textContent;
}

// ─── Navigation: color switching ─────────────────────────────────────────────

let colorGeneration = 0;

async function switchColor(targetUrl, sectionId, generation) {
  // First fetch — the color button's href may point to a different piece than the
  // one the user is currently viewing (e.g. they switched to Loveseat client-side
  // but the color button still links to the Sofa of the new color).
  const firstRes = await fetchSectionDoc(targetUrl, sectionId);
  if (!firstRes || generation !== colorGeneration) return;
  const firstDoc = firstRes.doc;

  // 1. Parse the new collection's panel data so we can inspect its member list
  const firstDataEl = firstDoc.querySelector('script[type="application/json"][id^="family-panel-data-"]');
  if (!firstDataEl) return;
  let firstData;
  try { firstData = JSON.parse(firstDataEl.textContent); } catch { return; }

  // 2. If a specific piece is active, find its match in the new collection.
  //    If the color button pointed elsewhere, do a second fetch for the right piece.
  let finalRes = firstRes;
  let finalDoc = firstDoc;
  let finalDataEl = firstDataEl;
  let finalData = firstData;

  if (activePieceName) {
    const matchingMember = firstData.members.find(m => m.pieceName === activePieceName);
    if (matchingMember && !matchingMember.isCurrent) {
      const pieceRes = await fetchSectionDoc(matchingMember.url, sectionId);
      if (generation !== colorGeneration) return;
      if (pieceRes) {
        const pieceDataEl = pieceRes.doc.querySelector('script[type="application/json"][id^="family-panel-data-"]');
        if (pieceDataEl) {
          try {
            finalData = JSON.parse(pieceDataEl.textContent);
            finalRes = pieceRes;
            finalDoc = pieceRes.doc;
            finalDataEl = pieceDataEl;
          } catch { /* fall through — use first fetch */ }
        }
      }
    }
  }

  // 3. Lock scroll position before any DOM mutations begin
  const savedScrollY = window.scrollY;

  // 4. Update URL to the correct piece in the new color
  const activeMember = finalData.members.find(m => m.isCurrent);
  history.pushState({}, '', activeMember ? activeMember.url : targetUrl);

  // 5. Swap product details
  const srcJson = finalDoc.querySelector('script[id^="ProductCardJson-"]');
  const dstJson = document.querySelector('script[id^="ProductCardJson-"]');
  if (srcJson && dstJson) dstJson.replaceWith(srcJson);

  swapElement('product-variants', finalDoc);
  swapElement('atc-submit-form', finalDoc);
  swapElement('[data-render-price]', finalDoc);
  swapElement('[data-render-sku]', finalDoc);
  swapElement('.main-product_main-heading', finalDoc);
  swapElement('.main-product_row--content', finalDoc);

  // Re-render the (separate) measurements section for the new product
  swapMeasurements(finalRes.measurementsHtml);

  const newTitle = finalDoc.querySelector('title');
  if (newTitle) document.title = newTitle.textContent;

  // 6. Swap panel JSON data element (piece click handler reads sectionId + members from here)
  const oldDataEl = document.querySelector('script[type="application/json"][id^="family-panel-data-"]');
  if (oldDataEl) oldDataEl.replaceWith(finalDataEl);

  // 7. Swap panel HTML (updates piece buttons and color active state in one shot)
  const newPanelEl = finalDoc.querySelector('[data-family-panel]');
  const oldPanelEl = document.querySelector('[data-family-panel]');
  if (newPanelEl && oldPanelEl) {
    oldPanelEl.replaceWith(newPanelEl);

    // Re-attach prefetch handlers to the new panel's piece buttons
    newPanelEl.querySelectorAll('[data-family-member-id]').forEach(card => {
      const startPrefetch = () => {
        if (card.classList.contains('product-family-panel_option-btn--active')) return;
        prefetchDoc(card.getAttribute('href'), finalData.sectionId);
      };
      card.addEventListener('mouseenter', startPrefetch);
      card.addEventListener('touchstart', startPrefetch, { passive: true });
    });
  }

  // 8. Rebuild gallery navigating to the correct piece. When the target piece has
  // no images of its own the rebuild lands on the collection hero with no slide
  // animation (index 0 → instant swap) — cross-fade it so it reads as a transition.
  const pieceHasImages = !!(activeMember && activeMember.images && activeMember.images.length);
  if (pieceHasImages) {
    resetGalleryFade(); // clear any leftover fade state from an interrupted switch
    buildGallery(finalData);
    requestAnimationFrame(() => window.scrollTo(0, savedScrollY));
  } else {
    await fadeOutGallery();
    if (generation !== colorGeneration) { fadeInGallery(); return; }
    buildGallery(finalData);
    requestAnimationFrame(() => window.scrollTo(0, savedScrollY));
    fadeInGallery();
  }
}

// ─── Initialisation ──────────────────────────────────────────────────────────

function initPanel(dataEl) {
  let data;
  try { data = JSON.parse(dataEl.textContent); }
  catch { return; }

  const { productId, sectionId } = data;

  const galleryId = `gallery-zoom-in-${sectionId}${productId}`;
  const mainEl = document.getElementById(galleryId);
  const thumbEl = document.querySelector(`[data-thumbnails-container="${sectionId}-${productId}"]`);

  if (!mainEl || !thumbEl) return;

  waitForSplide(mainEl, () => {
    // Store Splide references at module level for use by buildGallery / selectMember
    gallery.main = mainEl.splide;
    gallery.thumb = thumbEl.splide;
    gallery.thumbEl = thumbEl;

    buildGallery(data);

    // Seed active piece name from the initially-viewed member
    const currentMember = data.members.find(m => m.isCurrent);
    if (currentMember) activePieceName = currentMember.pieceName || null;

    // Attach prefetch handlers to piece buttons on initial load
    const panel = document.querySelector('[data-family-panel]');
    if (panel) {
      panel.querySelectorAll('[data-family-member-id]').forEach(card => {
        const startPrefetch = () => {
          if (card.classList.contains('product-family-panel_option-btn--active')) return;
          prefetchDoc(card.getAttribute('href'), sectionId);
        };
        card.addEventListener('mouseenter', startPrefetch);
        card.addEventListener('touchstart', startPrefetch, { passive: true });
      });

      // Scroll active button into view
      const activeBtn = panel.querySelector('.product-family-panel_option-btn--active');
      if (activeBtn) activeBtn.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  });
}

// ─── Global click handler ────────────────────────────────────────────────────
// Attached to document so it keeps working after panel HTML is replaced on color switch.

document.addEventListener('click', e => {

  // ── Piece switching ──
  const memberCard = e.target.closest('[data-family-member-id]');
  if (memberCard) {
    e.preventDefault();
    if (memberCard.classList.contains('product-family-panel_option-btn--active')) return;

    const panel = memberCard.closest('[data-family-panel]');
    if (!panel) return;

    const dataEl = document.querySelector('script[type="application/json"][id^="family-panel-data-"]');
    if (!dataEl) return;
    let data;
    try { data = JSON.parse(dataEl.textContent); } catch { return; }

    const member = data.members.find(m => String(m.id) === String(memberCard.dataset.familyMemberId));
    if (!member) return;

    selectMember(member, panel, data.sectionId, ++selectGeneration);
    return;
  }

  // ── Color switching ──
  // Color buttons are <a> tags with the panel button class but no data-family-member-id
  const colorBtn = e.target.closest('.product-family-panel_option-btn');
  if (colorBtn && colorBtn.tagName === 'A' && !colorBtn.dataset.familyMemberId) {
    e.preventDefault();
    if (colorBtn.classList.contains('product-family-panel_option-btn--active')) return;

    const dataEl = document.querySelector('script[type="application/json"][id^="family-panel-data-"]');
    if (!dataEl) return;
    let data;
    try { data = JSON.parse(dataEl.textContent); } catch { return; }

    const targetUrl = colorBtn.getAttribute('href');
    if (!targetUrl) return;

    switchColor(targetUrl, data.sectionId, ++colorGeneration);
  }
});

// ─── Boot ─────────────────────────────────────────────────────────────────────

document
  .querySelectorAll('script[type="application/json"][id^="family-panel-data-"]')
  .forEach(initPanel);

// Reload on browser back/forward to restore the correct server-rendered state
window.addEventListener('popstate', () => window.location.reload());
